
import {FC, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {v4 as uuidv4} from "uuid";
import {useGraphqlMutation, useGraphqlSubscription} from "../../../amplify/hooks";
import {TriggerResult} from "../../../amplify/hooks/types";
import {
  createSimpleOperationalHierarchyV2 as createSimpleOperationalHierarchyV2Mutation,
  createTenant as createTenantMutation,
  onCreateOperationalEntityV2 as onCreateOperationalEntitySubscription
} from "../../../amplify/graphql";
import {BackendError} from "@common-core/runtime-js/backend";
import {CreateOperationalEntityResultV2} from "@common-core/coat-operational-hierarchy-appsync-model";
import {
  ArrowUturnLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "@interstate/components/Icons";
import {Button} from "@interstate/components/Button";
import {Stepper, StepperLabels} from "@interstate/components/Stepper";
import {useAlertContext} from "../../../alerting";
import {useFeedback} from "../../../feedback";
import {useNavigation} from "../../../navigation";
import {EntitySourceType} from "../../EntitySourceType";
import {
  CreateTenantResponse,
  CreateTenantVariables,
  OnCreateOperationalEntityV2Data,
  OnCreateOperationalEntityV2Variables
} from "../../backend";
import {
  CreateSimpleOperationalHierarchyV2Data,
  CreateSimpleOperationalHierarchyV2Variables
} from "./backend";
import {CreateResultNode, Step, StepError, stepKey} from "./view-model";
import {usePlan} from "./context";
import {
  formatTenantCreationError,
  summarizeResult,
  updateResultNodes
} from "./helpers";
import {
  toCreateHierarchyInputV2,
  toCreateResultNode,
  toCreateResultNodeV2,
  toFailedResults,
  toQueuedResults,
  validateAndExtractTenantIdForHierarchy
} from "./transforms";
import {SelectSourcesStep} from "./steps/select-sources";
import {ReviewPlanStep} from "./steps/review-plan";
import {ResultsStep} from "./steps/results";
import "./HierarchyBuilder.scss";

const labelKey = (step: Step): string => {
  return `create-operational-hierarchy.stepper.step-labels.${stepKey(step)}`;
};

export interface HierarchyBuilderProps {
  simulateError?: boolean;
}

export const HierarchyBuilder: FC<HierarchyBuilderProps> = ({
  simulateError = false
}) => {
  const {t} = useTranslation();
  const [, setFeedback] = useFeedback();
  const [, setAlert, clearAlert] = useAlertContext();
  const {navigateToEntityFromEvent} = useNavigation();
  const {from, hierarchy, plan} = usePlan();
  const [activeStep, setActiveStep] = useState<Step>(Step.SELECT_SOURCES);
  const [transactionId] = useState<string>(() => uuidv4());
  const [working, setWorking] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [result, setResult] = useState<CreateResultNode>();
  const [stepError, setStepError] = useState<StepError>();

  // Store tenant validation error if it exists.
  const tenantValidationError = useMemo<Error | undefined>(() => {
    try {
      validateAndExtractTenantIdForHierarchy(hierarchy);
      return undefined;
    } catch (error) {
      return error instanceof Error ? error : new Error(String(error));
    }
  }, [hierarchy]);

  useEffect(() => {
    if (activeStep !== Step.REVIEW_PLAN) return;
    if (tenantValidationError) {
      setAlert({message: tenantValidationError.message});
    } else {
      clearAlert();
    }
  }, [activeStep, tenantValidationError, setAlert, clearAlert]);

  // Clear any error alerts when component unmounts so the alert doesn't persist onto other pages.
  useEffect(() => {
    return () => clearAlert();
  }, [clearAlert]);
  const [createSimpleOperationalHierarchyV2] = useGraphqlMutation<
    CreateSimpleOperationalHierarchyV2Data,
    CreateSimpleOperationalHierarchyV2Variables
  >({
    amplifyMutation: createSimpleOperationalHierarchyV2Mutation
  });
  const [createTenant] = useGraphqlMutation<
    CreateTenantResponse,
    CreateTenantVariables
  >({
    amplifyMutation: createTenantMutation
  });

  useGraphqlSubscription<
    OnCreateOperationalEntityV2Data,
    OnCreateOperationalEntityV2Variables
  >({
    amplifySubscription: onCreateOperationalEntitySubscription,
    variables: {transactionId},
    onData: data => {
      if (!data?.result) {
        return;
      }
      setResult(result => {
        if (result) {
          return updateResultNodes(result, toCreateResultNode(data.result));
        }
      });
    },
    onError: error => {
      console.error("Subscription error:", error);
    }
  });

  const assertResultsPresentV2 = (
    result: TriggerResult<CreateSimpleOperationalHierarchyV2Data>
  ): CreateOperationalEntityResultV2 => {
    if (!result.data?.result) {
      throw new BackendError(
        t("create-operational-hierarchy.errors.no-results"),
        500
      );
    }
    return result.data.result;
  };

  const onCreateHierarchyError = (error: any): void => {
    console.error(error);
    setStepError({step: Step.RESULTS, error});
    setAlert({message: error.message});
  };

  const showSuccessFeedback = (): void => {
    setFeedback({
      type: "success",
      message: t("create-operational-hierarchy.feedback.success"),
      autoHide: true,
      onClose: () => setFeedback(undefined)
    });
  };

  const onCreateHierarchySuccess = (): void => {
    setCompleted(true);
    showSuccessFeedback();
  };

  const saveResult = (result: CreateResultNode): CreateResultNode => {
    // Reminder: state mutators do not update the current state; it only updates AFTER the triggered render cycle
    setResult(result);
    return result;
  };

  const determineOutcome = (result: CreateResultNode) => {
    const summary = summarizeResult(result);
    if (summary.failed.length > 0) {
      window.pendo?.track("Simple Hierarchy Failed", {
        created: summary.created.length,
        operational: summary.operational.length,
        failed: summary.failed.length,
        skipped: summary.skipped.length
      });
      // Report the first error to the user
      const failure = summary.failed[0];
      throw new Error(
        failure.message || t("create-operational-hierarchy.errors.generic")
      );
    } else {
      window.pendo?.track("Simple Hierarchy Created", {
        created: summary.created.length,
        operational: summary.operational.length,
        failed: summary.failed.length,
        skipped: summary.skipped.length
      });
      onCreateHierarchySuccess();
    }
  };

  /**
   * Creates a tenant if one does not already exist for the hierarchy.
   * @returns The tenant ID (existing or newly created).
   * @throws If tenant creation fails or returns no tenant ID.
   */
  const createTenantIfRequired = async (
    tenantId: string | undefined
  ): Promise<string> => {
    if (tenantId !== undefined) {
      return tenantId;
    }

    const ultimateParent = hierarchy.hierarchy || hierarchy;
    const tenantName = ultimateParent.custName;

    let result;
    try {
      result = await createTenant({
        variables: {input: {name: tenantName}}
      });
    } catch (error) {
      throw error instanceof Error
        ? formatTenantCreationError(error as Error, tenantName, t)
        : new Error(
            t(
              "create-operational-hierarchy.errors.tenant.creation-failed-detail",
              {tenantName}
            )
          );
    }

    if (!result.data?.createTenant?.tenant?.id) {
      throw new Error(
        t("create-operational-hierarchy.errors.tenant.no-tenant-id-detail")
      );
    }

    return result.data.createTenant.tenant.id;
  };

  const createHierarchyV2 = async () => {
    console.log(`Transaction ID (V2): ${transactionId}`);

    let tenantId: string | undefined;
    try {
      tenantId = validateAndExtractTenantIdForHierarchy(hierarchy);
    } catch (error) {
      onCreateHierarchyError(error);
      return;
    }

    const input = toCreateHierarchyInputV2(plan, transactionId, tenantId ?? "");
    if (!input) {
      onCreateHierarchyError(
        new Error(t("create-operational-hierarchy.errors.invalid-plan"))
      );
      return;
    }

    setResult(toQueuedResults(input));
    setActiveStep(Step.RESULTS);
    setFeedback(undefined);
    clearAlert();
    setCompleted(false);
    setWorking(true);
    setStepError(undefined);

    if (simulateError) {
      setWorking(false);
      setResult(toFailedResults(input));
      onCreateHierarchyError(new Error("No soup for you!"));
      return;
    }

    // TODO: This could be added to promise chain
    // Create tenant first if needed (undefined = new tenant required)
    let resolvedTenantId: string;
    try {
      resolvedTenantId = await createTenantIfRequired(tenantId);
    } catch (error) {
      setWorking(false);
      setResult(toFailedResults(input));
      onCreateHierarchyError(error);
      return;
    }

    const resolvedInput = toCreateHierarchyInputV2(
      plan,
      transactionId,
      resolvedTenantId
    );
    if (!resolvedInput) {
      onCreateHierarchyError(
        new Error(t("create-operational-hierarchy.errors.invalid-plan"))
      );
      setWorking(false);
      return;
    }

    createSimpleOperationalHierarchyV2({variables: {input: resolvedInput}})
      .then(assertResultsPresentV2)
      .then(toCreateResultNodeV2)
      .then(saveResult)
      .then(determineOutcome)
      .catch(error => {
        setResult(toFailedResults(resolvedInput));
        onCreateHierarchyError(error);
      })
      .finally(() => setWorking(false));
  };

  const onCreateButtonClick = () => {
    createHierarchyV2();
  };

  const stepperLabel = (step: Step): StepperLabels => {
    const defaults: StepperLabels = {
      slotProps: {
        button: {
          dataAttr: {
            "data-testid": `${stepKey(step)}-stepper-button`
          }
        }
      }
    };
    // Check if there is an error for the current step
    if (stepError && stepError.step === step) {
      return {
        ...defaults,
        label: t("create-operational-hierarchy.stepper.step-labels.error"),
        error: true
      };
    }
    // This forces the completed state when there are results
    if (activeStep === Step.RESULTS) {
      return {
        ...defaults,
        label: t(labelKey(step)),
        completed
      };
    }
    return {
      ...defaults,
      label: t(labelKey(step))
    };
  };

  const renderActiveStep = () => {
    switch (activeStep) {
      case Step.SELECT_SOURCES:
        return <SelectSourcesStep />;
      case Step.REVIEW_PLAN:
        return <ReviewPlanStep />;
      case Step.RESULTS:
        return <ResultsStep result={result!} />;
    }
  };

  return (
    <section className={"hierarchy-builder"}>
      <Stepper
        data-testid={"hierarchy-stepper"}
        activeStep={activeStep}
        labels={[
          stepperLabel(Step.SELECT_SOURCES),
          stepperLabel(Step.REVIEW_PLAN),
          stepperLabel(Step.RESULTS)
        ]}
      />
      {renderActiveStep()}
      <div data-testid={"stepper-controls"} className={"stepper-controls"}>
        <div
          data-testid={"stepper-controls-left"}
          className={"stepper-controls-left"}>
          {activeStep === Step.REVIEW_PLAN && (
            <Button
              id={"stepper-navigation-previous-button"}
              data-testid={"stepper-navigation-previous-button"}
              buttonStyle={"secondary"}
              className={"stepper-navigation-button"}
              startIcon={<ChevronLeftIcon />}
              onClick={() => {
                setStepError(undefined);
                setActiveStep(Step.SELECT_SOURCES);
              }}>
              {t(labelKey(Step.SELECT_SOURCES))}
            </Button>
          )}
          {activeStep === Step.RESULTS && stepError && (
            <Button
              id={"stepper-navigation-previous-button"}
              data-testid={"stepper-navigation-previous-button"}
              buttonStyle={"secondary"}
              className={"stepper-navigation-button"}
              startIcon={<ChevronLeftIcon />}
              onClick={() => {
                setStepError(undefined);
                clearAlert();
                setActiveStep(Step.REVIEW_PLAN);
              }}>
              {t(labelKey(Step.REVIEW_PLAN))}
            </Button>
          )}
        </div>
        <div
          data-testid={"stepper-controls-right"}
          className={"stepper-controls-right"}>
          {(activeStep !== Step.RESULTS ||
            (activeStep === Step.RESULTS && stepError)) && (
            <Button
              id={"step-action-cancel-button"}
              data-testid={"step-action-cancel-button"}
              className={"step-action-button"}
              buttonStyle={"tertiary"}
              disabled={working}
              onClick={event => {
                navigateToEntityFromEvent(
                  event,
                  from.caId,
                  EntitySourceType.CLIENT_HIERARCHY
                );
              }}>
              {t(
                "create-operational-hierarchy.stepper.controls.actions.cancel"
              )}
            </Button>
          )}
          {activeStep === Step.SELECT_SOURCES && (
            <Button
              id={"stepper-navigation-next-button"}
              data-testid={"stepper-navigation-next-button"}
              buttonStyle={"primary"}
              className={"stepper-navigation-button"}
              endIcon={<ChevronRightIcon />}
              onClick={() => setActiveStep(Step.REVIEW_PLAN)}>
              {t(labelKey(Step.REVIEW_PLAN))}
            </Button>
          )}
          {activeStep === Step.RESULTS && !stepError && !working && (
            <Button
              id={"back-to-client-button"}
              data-testid={"back-to-client-button"}
              className={"back-to-client-button"}
              buttonStyle={"tertiary"}
              startIcon={<ArrowUturnLeftIcon />}
              onClick={event => {
                navigateToEntityFromEvent(
                  event,
                  from.caId,
                  EntitySourceType.CLIENT_HIERARCHY
                );
              }}>
              {t("create-operational-hierarchy.back-to-client-entity")}
            </Button>
          )}
          {activeStep === Step.REVIEW_PLAN && (
            <Button
              id={"step-action-create-button"}
              data-testid={"step-action-create-button"}
              className={"step-action-button"}
              buttonStyle={"primary"}
              endIcon={<ChevronRightIcon />}
              onClick={onCreateButtonClick}
              disabled={!!tenantValidationError}
              isLoading={working}>
              {t(
                "create-operational-hierarchy.stepper.controls.actions.create"
              )}
            </Button>
          )}
          {((activeStep === Step.RESULTS && stepError) ||
            (activeStep === Step.RESULTS && working)) && (
            <Button
              id={"step-action-create-button"}
              data-testid={"step-action-create-button"}
              className={"step-action-button"}
              buttonStyle={"primary"}
              onClick={onCreateButtonClick}
              isLoading={working}>
              {t(
                "create-operational-hierarchy.stepper.controls.actions.create"
              )}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
